import { useEffect } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import { cashTransactionSchema, type CashTransactionFormData } from '@/validators/cash'
import { useCreateCashTransaction } from '@/hooks/useCashTransactions'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { useCashCategories } from '@/hooks/useCashCategories'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
)

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

interface CashTransactionModalProps {
  open: boolean
  onClose: () => void
  defaultAccountId?: string
}

export function CashTransactionModal({ open, onClose, defaultAccountId }: CashTransactionModalProps) {
  const createMutation = useCreateCashTransaction()
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const accounts = accountsData?.data ?? []

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CashTransactionFormData>({
    resolver: zodResolver(cashTransactionSchema),
    defaultValues: {
      accountId: '',
      categoryId: '',
      type: 'OUTFLOW',
      amount: '' as unknown as number,
      date: todayISO(),
      description: '',
    },
  })

  const type = useWatch({ control, name: 'type' })
  const { data: categoriesData } = useCashCategories({
    kind: type,
    active: true,
    perPage: 100,
  })
  const categories = categoriesData?.data ?? []

  useEffect(() => {
    if (open) {
      const fallbackAccount =
        defaultAccountId ?? accounts.find((a) => a.isDefault)?.id ?? accounts[0]?.id ?? ''
      reset({
        accountId: fallbackAccount,
        categoryId: '',
        type: 'OUTFLOW',
        amount: '' as unknown as number,
        date: todayISO(),
        description: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function onSubmit(data: CashTransactionFormData) {
    try {
      await createMutation.mutateAsync({
        ...data,
        date: new Date(data.date).toISOString(),
      })
      toast.success('Lançamento registrado!')
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao registrar lançamento'))
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Novo lançamento
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-2">
                  {(['INFLOW', 'OUTFLOW'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        field.onChange(t)
                        setValue('categoryId', '')
                      }}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                        field.value === t
                          ? t === 'INFLOW'
                            ? 'border-green-600 bg-green-50 text-green-800'
                            : 'border-red-600 bg-red-50 text-red-800'
                          : 'border-input text-muted-foreground hover:bg-muted/50',
                      )}
                    >
                      {t === 'INFLOW' ? 'Entrada' : 'Saída'}
                    </button>
                  ))}
                </div>
              )}
            />

            <div className="space-y-1.5">
              <Label htmlFor="ct-account">Conta *</Label>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <select id="ct-account" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.accountId && (
                <p className="text-xs text-destructive">{errors.accountId.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ct-category">Categoria *</Label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <select id="ct-category" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {categories.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Nenhuma categoria de {type === 'INFLOW' ? 'receita' : 'despesa'} cadastrada.
                </p>
              )}
              {errors.categoryId && (
                <p className="text-xs text-destructive">{errors.categoryId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ct-amount">Valor *</Label>
                <MoneyInput id="ct-amount" error={!!errors.amount} {...register('amount')} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-date">Data *</Label>
                <Input
                  id="ct-date"
                  type="date"
                  aria-invalid={!!errors.date}
                  {...register('date')}
                />
                {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ct-description">Descrição *</Label>
              <Input
                id="ct-description"
                placeholder="Ex.: Aluguel do escritório"
                aria-invalid={!!errors.description}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={createMutation.isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={createMutation.isPending} loadingText="Registrando…">
                Registrar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
